import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  CategoryEvent,
  CategoryEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/inventory/v1/category_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { CategoryCreatedDetail } from './CategoryCreatedDetail';
import { CategoryDeletedDetail } from './CategoryDeletedDetail';
import { CategoryUpdatedDetail } from './CategoryUpdatedDetail';

import { useTranslations } from '@/inventory/i18n/useTranslations';

type CategoryEventDetailProps = {
  payload: string;
};

export const CategoryEventDetail = ({ payload }: CategoryEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): CategoryEvent | null => {
    try {
      return fromJson(CategoryEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <CategoryCreatedDetail event={inner.value} />;
      case 'updated':
        return <CategoryUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <CategoryDeletedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.categoryCreated;
      case 'updated':
        return t.logs.events.categoryUpdated;
      case 'deleted':
        return t.logs.events.categoryDeleted;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@CategoryEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
